import { t } from "@lingui/macro";
import type {
  Contact,
  CreateContactInput,
  UpdateContactInput,
} from "@src/__generated__/urql-graphql";
import { email, required } from "@src/utils/forms/validators";
import { FieldState, FormState } from "formstate";

type TMode = "create" | "update";

export class Form {
  private emailRequiredWhenNoPhone = ($: string) => {
    if (!$ && !this.phone.value) {
      return t`The email is required when phone is empty`;
    }

    return null;
  };

  private phoneRequiredWhenNoEmail = ($: string) => {
    if (!$ && !this.email.value) {
      return t`The phone is required when email is empty`;
    }

    return null;
  };

  brand_id = new FieldState("").validators(required);
  first_name = new FieldState("").validators(required);
  last_name = new FieldState("").validators(required);
  email = new FieldState("").validators(this.emailRequiredWhenNoPhone, email);
  phone = new FieldState("").validators(this.phoneRequiredWhenNoEmail);

  _form = new FormState([
    this.brand_id,
    this.first_name,
    this.last_name,
    this.email,
    this.phone,
  ]);

  constructor(
    contact?: Pick<Contact, "first_name" | "last_name" | "email" | "phone">,
    brand_id?: string,
  ) {
    if (contact) {
      contact.first_name && this.first_name.onChange(contact.first_name);
      contact.last_name && this.last_name.onChange(contact.last_name);
      contact.email && this.email.onChange(contact.email);
      contact.phone && this.phone.onChange(contact.phone);
    }

    if (brand_id) {
      this.brand_id.onChange(brand_id);
    }
  }

  validate(mode: TMode) {
    let fields: FieldState<any>[] = [
      this.first_name,
      this.last_name,
      this.email,
      this.phone,
    ];

    if (mode === "create") {
      fields = [...fields, this.brand_id];
    }

    return new FormState(fields).validate();
  }

  private serializeCommon() {
    return {
      first_name: this.first_name.$,
      last_name: this.last_name.$,
      email: this.email.$.trim(),
      phone: this.phone.$,
    };
  }

  serializeForCreate(): CreateContactInput {
    return { brand_id: this.brand_id.$, ...this.serializeCommon() };
  }

  serializeForUpdate(id: string): UpdateContactInput {
    return { id, ...this.serializeCommon() };
  }

  reset() {
    this.first_name.reset();
    this.last_name.reset();
    this.email.reset();
    this.phone.reset();
  }
}
